import axios from 'axios';

import {
	getAuth,
	sendEmailVerification,
	sendPasswordResetEmail,
} from 'firebase/auth';
import { UpdateAddressPayload, UserProfile } from '../interfaces/user.interface';


const API_URL = `${process.env.REACT_APP_BACKEND}`;

export const signUpUser = async (userData: any) => {
	return axios
		.post(`${API_URL}/user/register`, userData)
		.then((response) => response.data);
};

export const verifyUserEmail = async (email: string) => {
	return axios.patch(`${API_URL}/verify-email`, { email });
};

export const resendVerificationEmail = async () => {
	const auth = getAuth();
	const user = auth.currentUser;

	if (user && !user.emailVerified) {
		await sendEmailVerification(user, {
			url: 'http://localhost:3000/verify',
			handleCodeInApp: true,
		});
	} else {
		throw new Error('No user logged in or email already verified');
	}
};

export const resetPassword = async (email: string): Promise<void> => {
	const auth = getAuth();

	try {
		await sendPasswordResetEmail(auth, email);
	} catch (error: any) {
		console.error('Error sending reset email:', error.message);
		throw error;
	}
};

export const changePassword = async (currentPassword: string, newPassword: string) => {
	const token = localStorage.getItem('firebaseToken');
	if (!token) {
		throw new Error('No authentication token found');
	}

	try {
		const response = await axios.post(
			`${API_URL}/auth/change-password`,
			{
				currentPassword,
				newPassword
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			}
		);
		return response.data;
	} catch (error: any) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					throw new Error('Contraseña actual incorrecta');
				case 400:
					throw new Error('La nueva contraseña no cumple con los requisitos');
				default:
					throw new Error('Error al cambiar la contraseña');
			}
		}
		throw new Error('Error de conexión. Por favor, intente nuevamente.');
	}
};

export const getUserProfile = async (): Promise<UserProfile> => {
	const token = localStorage.getItem('firebaseToken');
	if (!token) {
		throw new Error('No authentication token found');
	}

	try {
		const response = await axios.get(`${API_URL}/auth/me`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		const userData = response.data;
		return userData;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 401) {
				throw new Error('Token inválido o faltante');
			}
			if (error.response?.status === 404) {
				throw new Error('Usuario no encontrado');
			}
		}
		throw error;
	}
};

export const updateUserAddress = async (data: UpdateAddressPayload): Promise<void> => {
	const token = localStorage.getItem('firebaseToken');
	if (!token) {
		throw new Error('No authentication token found');
	}

	try {
		await axios.patch(
			`${API_URL}/user/address`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);
	} catch (error: any) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					throw new Error('No autorizado. Por favor, inicie sesión nuevamente.');
				case 404:
					throw new Error('Usuario no encontrado.');
				default:
					throw new Error('Error al actualizar la información del usuario.');
			}
		}
		throw new Error('Error de conexión. Por favor, intente nuevamente.');
	}
};

export const updateUserTerms = async (
	email: string,
	acceptedTermsId: number,
) => {
	const token = localStorage.getItem('firebaseToken');
	if (!token) {
		throw new Error('No authentication token found');
	}

	try {
		await axios.patch(
			`${API_URL}/user/update-accepted-terms`,
			{ acceptedTermsId },
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);
	} catch (error: any) {
		console.error(
			'Error updating Terms and Conditions acceptance:',
			error.message,
		);
		throw error;
	}
};
